export type AiProvider = 'gemini' | 'openai';
export type FrontendAiFeature =
  | 'portfolioAnalysis'
  | 'analystChat'
  | 'latestQuote'
  | 'dividendData'
  | 'csvMapping'
  | 'firePlan';

const DEFAULT_PROVIDER: AiProvider = 'openai';
const DEFAULT_MODELS: Record<AiProvider, string> = {
  gemini: 'gemini-2.5-flash-lite',
  openai: 'gpt-5-mini',
};

const LIGHT_FEATURES = new Set<FrontendAiFeature>(['latestQuote', 'dividendData', 'csvMapping']);
const STOCK_ADVICE_FEATURES = new Set<FrontendAiFeature>(['portfolioAnalysis', 'analystChat', 'firePlan']);

const normalizeProvider = (provider?: string): AiProvider =>
  provider === 'gemini' || provider === 'openai' ? provider : DEFAULT_PROVIDER;

const getEnvValue = (key: string): string | undefined => {
  const value = (import.meta.env as Record<string, string | undefined>)[key];
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
};

const getFrontendAiProvider = (): AiProvider => normalizeProvider(getEnvValue('VITE_AI_PROVIDER'));

const getProviderDefaultModel = (provider: AiProvider): string => {
  const envKey = provider === 'openai' ? 'VITE_OPENAI_MODEL' : 'VITE_GEMINI_MODEL';
  return getEnvValue(envKey) || DEFAULT_MODELS[provider];
};

const getFeatureModelOverride = (feature: FrontendAiFeature, provider: AiProvider): string | undefined => {
  if (provider === 'openai') {
    if (LIGHT_FEATURES.has(feature)) return getEnvValue('VITE_OPENAI_MODEL_LIGHT');
    if (STOCK_ADVICE_FEATURES.has(feature)) return getEnvValue('VITE_OPENAI_MODEL_STOCK_ADVICE');
    return undefined;
  }

  if (LIGHT_FEATURES.has(feature)) return getEnvValue('VITE_GEMINI_MODEL_LIGHT');
  if (STOCK_ADVICE_FEATURES.has(feature)) return getEnvValue('VITE_GEMINI_MODEL_STOCK_ADVICE');
  return undefined;
};

export const getFrontendAiSelection = (feature: FrontendAiFeature) => {
  const provider = getFrontendAiProvider();
  return {
    feature,
    provider,
    model: getFeatureModelOverride(feature, provider) || getProviderDefaultModel(provider),
  };
};
